import React from 'react';
import { X, Minus, Plus, ArrowRight } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { useNavigate } from 'react-router-dom';
import { openWhatsApp, WHATSAPP_MESSAGES } from '../../utils/whatsapp';

export const CartDrawer = () => {
  const { 
    cartItems, 
    isCartOpen, 
    setIsCartOpen, 
    removeFromCart, 
    updateQuantity, 
    getCartTotal 
  } = useCart();
  const navigate = useNavigate();

  if (!isCartOpen) return null;

  const handleCheckout = () => {
    setIsCartOpen(false);
    navigate('/checkout');
  };

  const handleViewCart = () => {
    setIsCartOpen(false);
    navigate('/checkout');
  };

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-40 z-40 transition-opacity"
        onClick={() => setIsCartOpen(false)}
      />

      {/* Drawer */}
      <div 
        data-testid="cart-drawer"
        className="fixed right-0 top-0 h-full w-full max-w-md bg-white z-50 shadow-2xl flex flex-col"
        style={{ transform: isCartOpen ? 'translateX(0)' : 'translateX(100%)' }}
      >
        {/* Header */}
        <div 
          className="flex items-center justify-between px-6 py-5"
          style={{ borderBottom: '1px solid #E8E6E1' }}
        >
          <h2 
            className="font-serif text-xl"
            style={{ color: '#2B2B2B' }}
          >
            Your Cart
          </h2>
          <button 
            onClick={() => setIsCartOpen(false)}
            className="p-1 hover:opacity-70 transition-opacity"
            data-testid="close-cart"
          >
            <X size={20} strokeWidth={1.5} style={{ color: '#2B2B2B' }} />
          </button>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {cartItems.length === 0 ? (
            <p 
              className="text-sm text-center py-12"
              style={{ color: '#6B6B6B' }}
            >
              Your cart is empty
            </p>
          ) : (
            <div className="space-y-4">
              {cartItems.map(item => (
                <div 
                  key={item.id}
                  className="flex gap-4 py-4"
                  style={{ borderBottom: '1px solid #F0EEE9' }}
                  data-testid={`cart-item-${item.id}`}
                >
                  {/* Item Image */}
                  <div 
                    className="w-20 h-20 flex-shrink-0 flex items-center justify-center"
                    style={{ backgroundColor: '#F5F2EB' }}
                  >
                    {item.image ? (
                      <picture>
                        <source srcSet={item.image.replace(/\.(png|jpg|jpeg|PNG)$/i, '.webp')} type="image/webp" />
                        <img 
                          src={item.image} 
                          alt={item.name}
                          className="w-full h-full object-contain"
                          style={{ objectFit: 'contain', objectPosition: 'center' }}
                        />
                      </picture>
                    ) : (
                      <div className="w-full h-full" style={{ backgroundColor: '#F0EEE9' }} />
                    )}
                  </div>

                  {/* Item Details */}
                  <div className="flex-1">
                    <h3 
                      className="text-sm font-medium mb-1"
                      style={{ color: '#2B2B2B' }}
                    >
                      {item.name}
                    </h3>
                    <p 
                      className="text-sm font-semibold mb-3"
                      style={{ color: '#2B2B2B' }}
                    >
                      {item.price}
                    </p>

                    {/* Quantity Controls */}
                    <div className="flex items-center justify-between">
                      <div 
                        className="flex items-center gap-3"
                        style={{ border: '1px solid #E8E6E1' }}
                      >
                        <button 
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          className="p-2 hover:bg-gray-50 transition-colors"
                          data-testid={`decrease-${item.id}`}
                        >
                          <Minus size={14} style={{ color: '#2B2B2B' }} />
                        </button>
                        <span 
                          className="text-sm font-medium w-6 text-center"
                          style={{ color: '#2B2B2B' }}
                        >
                          {item.quantity}
                        </span>
                        <button 
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          className="p-2 hover:bg-gray-50 transition-colors"
                          data-testid={`increase-${item.id}`}
                        >
                          <Plus size={14} style={{ color: '#2B2B2B' }} />
                        </button>
                      </div>

                      <button 
                        onClick={() => removeFromCart(item.id)}
                        className="text-xs hover:opacity-70 transition-opacity"
                        style={{ color: '#6B6B6B' }}
                        data-testid={`remove-${item.id}`}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {cartItems.length > 0 && (
          <div 
            className="px-6 py-5"
            style={{ borderTop: '1px solid #E8E6E1' }}
          >
            {/* Subtotal */}
            <div className="flex items-center justify-between mb-5">
              <span 
                className="text-sm"
                style={{ color: '#6B6B6B' }}
              >
                Subtotal
              </span>
              <span 
                className="text-lg font-semibold"
                style={{ color: '#2B2B2B' }}
              >
                ₹{getCartTotal().toLocaleString()}
              </span>
            </div>

            {/* CTAs */}
            <div className="space-y-3">
              <button
                onClick={handleCheckout}
                className="w-full py-3.5 text-sm font-medium transition-all duration-200 hover:opacity-90 flex items-center justify-center gap-2"
                style={{ 
                  backgroundColor: '#1A3D36', 
                  color: '#FFFFFF',
                  borderRadius: '4px'
                }}
                data-testid="checkout-btn"
              >
                Checkout
                <ArrowRight size={15} />
              </button>
              <button
                onClick={() => openWhatsApp(WHATSAPP_MESSAGES.cart)}
                className="w-full py-3.5 text-sm font-medium transition-all duration-200 hover:opacity-80"
                style={{ 
                  backgroundColor: 'transparent', 
                  color: '#1A3D36',
                  border: '1px solid #1A3D36',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
                data-testid="cart-whatsapp-btn"
              >
                Confirm My Protocol on WhatsApp
              </button>
              <button
                onClick={handleViewCart}
                className="w-full py-3.5 text-sm font-medium transition-all duration-200 hover:opacity-80"
                style={{ 
                  backgroundColor: 'transparent', 
                  color: '#2B2B2B',
                  border: '1px solid #D9D6CF',
                  borderRadius: '4px'
                }}
                data-testid="view-cart-btn"
              >
                View Cart
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default CartDrawer;
